import { useState, useEffect, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Trophy, Medal, Award, Users, Heart, HeartOff } from "lucide-react";
import { ChessAppLayout } from "@/components/ChessAppLayout";
import { AvatarDisplay } from "@/components/AvatarDisplay";
import { SeasonalRewardsCard } from "@/components/SeasonalRewardsCard";
import { useAuth } from "@/hooks/useAuth";
import { useSEO } from '@/hooks/useSEO';
import { useFakeFriendships } from "@/hooks/useFakeFriendships";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { generateFakeLeaderboard, FakePlayer } from "@/lib/fake-leaderboard-generator";
import { generateAvatarFromUsername } from "@/lib/avatar-config";
import { 
  applyPeriodVariance, 
  getPeriodDisplayName, 
  type LeaderboardPeriod 
} from "@/lib/seasonal-rankings";

interface LeaderboardEntry {
  id: string;
  username: string;
  display_name?: string;
  elo_rating: number;
  games_played: number;
  games_won: number;
  games_lost: number;
  games_drawn: number;
  isFake?: boolean;
  avatarConfig?: any;
}

export default function Leaderboards() {
  // SEO Configuration
  useSEO({
    title: "Chess Leaderboards - Top Players Rankings | ChessHatch",
    description: "View the top chess players on ChessHatch. Check ELO ratings, win rates, and compete with players worldwide in our friendly chess community.",
    keywords: "chess leaderboards, chess rankings, chess players, elo ratings, chess competition, top chess players",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Chess Leaderboards",
      "description": "Rankings of top chess players by ELO rating",
      "mainEntity": {
        "@type": "Table",
        "name": "Chess Player Rankings",
        "description": "Leaderboard showing top chess players ranked by ELO rating"
      }
    }
  });

  const [realPlayers, setRealPlayers] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState<LeaderboardPeriod>('all-time');
  const { user, loading: authLoading } = useAuth();
  const { isFakeFriend, toggleFakeFriend } = useFakeFriendships();

  // Generate fake leaderboard (memoized for consistency)
  const fakePlayers = useMemo(() => generateFakeLeaderboard(), []);

  // Merge real and fake players, sorted by ELO with period variance
  const allPlayers = useMemo(() => {
    const realWithFlag = realPlayers.map(p => ({ 
      ...p, 
      isFake: false,
      avatarConfig: p.avatarConfig || generateAvatarFromUsername(p.username || 'Anonymous')
    }));
    
    // Apply period variance to fake players
    const fakeWithVariance = fakePlayers.map(p => ({
      ...p,
      elo_rating: applyPeriodVariance(p.elo_rating, p.id, period)
    }));
    
    const combined = [...realWithFlag, ...fakeWithVariance];
    return combined.sort((a, b) => b.elo_rating - a.elo_rating).slice(0, 50);
  }, [realPlayers, fakePlayers, period]);

  // Find user's rank
  const userRank = useMemo(() => {
    if (!user) return undefined;
    const allSorted = [...realPlayers.map(p => ({ ...p, isFake: false })), ...fakePlayers]
      .sort((a, b) => b.elo_rating - a.elo_rating);
    const index = allSorted.findIndex(p => p.id === user.id);
    return index >= 0 ? index + 1 : undefined;
  }, [realPlayers, fakePlayers, user]);

  const totalPlayerCount = realPlayers.length + 973;

  useEffect(() => {
    if (authLoading) return;
    if (user) {
      fetchLeaderboards();
    } else {
      setLoading(false);
    }
  }, [user, authLoading]);

  const fetchLeaderboards = async () => {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('id, username, display_name, elo_rating, games_played, games_won, games_lost, games_drawn, avatar_config')
        .order('elo_rating', { ascending: false })
        .limit(100); // Fetch more real players to mix in

      if (error) throw error;
      setRealPlayers(data || []);
    } catch (error) {
      console.error('Error fetching leaderboards:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePlayerClick = (player: LeaderboardEntry, e: React.MouseEvent) => {
    if (player.isFake) {
      e.preventDefault();
      // Navigate to fake player profile instead of blocking
    }
  };

  const handleFriendToggle = (player: LeaderboardEntry, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!player.isFake) return;
    
    const added = toggleFakeFriend(player.id, player.username);
    toast.success(added 
      ? `Added ${player.username} to friends!` 
      : `Removed ${player.username} from friends`
    );
  };

  const getRankIcon = (position: number) => {
    if (position === 1) return <Trophy className="w-5 h-5 text-yellow-500" />;
    if (position === 2) return <Medal className="w-5 h-5 text-gray-400" />;
    if (position === 3) return <Award className="w-5 h-5 text-amber-600" />;
    return <span className="w-5 h-5 flex items-center justify-center text-muted-foreground font-bold">{position}</span>;
  };

  const getWinRate = (won: number, total: number) => {
    return total > 0 ? Math.round((won / total) * 100) : 0;
  };

  if (loading) {
    return (
      <ChessAppLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </ChessAppLayout>
    );
  }

  if (!user) {
    return (
      <ChessAppLayout>
        <div className="container mx-auto py-8 px-4">
          <div className="max-w-xl mx-auto text-center">
            <Card>
              <CardHeader>
                <CardTitle>Sign in to view Leaderboards</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground mb-4">Leaderboards are available to authenticated players.</p>
                <Button onClick={() => (window.location.href = '/auth')}>Sign In</Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </ChessAppLayout>
    );
  }

  return (
    <ChessAppLayout>
      <div className="container mx-auto py-8 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold mb-2 bg-gradient-to-r from-teen-gold via-teen-orange to-teen-pink bg-clip-text text-transparent">🏆 Leaderboards</h1>
            <p className="text-muted-foreground flex items-center justify-center gap-2">
              <Users className="w-4 h-4" />
              <span className="text-teen-gold font-semibold">{totalPlayerCount.toLocaleString()} players</span> ranked by ELO rating
            </p>
          </div>

          {/* Seasonal Rewards Card */}
          <SeasonalRewardsCard userRank={userRank} />

          {/* Period Tabs */}
          <Tabs value={period} onValueChange={(v) => setPeriod(v as LeaderboardPeriod)} className="mb-6">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="all-time">All-Time</TabsTrigger>
              <TabsTrigger value="season">Season</TabsTrigger>
              <TabsTrigger value="monthly">Monthly</TabsTrigger>
              <TabsTrigger value="weekly">Weekly</TabsTrigger>
            </TabsList>
          </Tabs>

          <Card variant="glass" className="border-primary/20">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Trophy className="w-5 h-5" />
                Top 50 Players - {getPeriodDisplayName(period)}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {allPlayers.map((player, index) => (
                  <div
                    key={player.id}
                    className={`flex items-center justify-between p-4 rounded-lg border transition-all duration-200 hover:scale-[1.01] ${
                      index === 0 ? 'bg-gradient-to-r from-teen-gold/20 to-yellow-500/10 border-teen-gold/40 shadow-teen-gold' :
                      index === 1 ? 'bg-gradient-to-r from-slate-400/20 to-zinc-400/10 border-slate-400/40' :
                      index === 2 ? 'bg-gradient-to-r from-orange-500/20 to-amber-600/10 border-orange-500/40' :
                      'bg-card/50 hover:bg-card border-border hover:border-primary/30'
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      {getRankIcon(index + 1)}
                      
                      {/* Avatar */}
                      <AvatarDisplay
                        username={player.username || 'Anonymous'}
                        avatarConfig={player.avatarConfig}
                        size="sm"
                      />
                      
                      <div>
                        <div className="flex items-center gap-2">
                          <Link 
                            to={`/player/${player.id}`}
                            onClick={(e) => handlePlayerClick(player, e)}
                            className={`font-semibold hover:underline transition-colors ${
                              index === 0 ? 'text-teen-gold hover:text-teen-gold/80' :
                              index === 1 ? 'text-slate-600 dark:text-slate-300 hover:text-slate-500' :
                              index === 2 ? 'text-orange-600 dark:text-orange-400 hover:text-orange-500' :
                              'hover:text-primary'
                            }`}
                          >
                            {player.username || player.display_name || 'Anonymous Player'}
                          </Link>
                          
                          {/* Friend indicator for fake players */}
                          {player.isFake && isFakeFriend(player.id) && (
                            <Badge variant="outline" className="text-xs py-0 px-1 text-teen-pink border-teen-pink/30">
                              <Heart className="w-3 h-3 mr-1 fill-teen-pink" />
                              Friend
                            </Badge>
                          )}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {player.games_played} games played
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      {/* Friend toggle for fake players */}
                      {player.isFake && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={(e) => handleFriendToggle(player, e)}
                        >
                          {isFakeFriend(player.id) ? (
                            <HeartOff className="w-4 h-4 text-muted-foreground" />
                          ) : (
                            <Heart className="w-4 h-4 text-muted-foreground hover:text-teen-pink" />
                          )}
                        </Button>
                      )}
                      
                      <div className="text-right hidden sm:block">
                        <div className="text-sm text-muted-foreground">Win Rate</div>
                        <div className="font-semibold">
                          {getWinRate(player.games_won, player.games_played)}%
                        </div>
                      </div>
                      
                      <div className="text-right hidden md:block">
                        <div className="text-sm text-muted-foreground">Record</div>
                        <div className="text-sm">
                          <span className="text-teen-green">{player.games_won}W</span>
                          {' '}
                          <span className="text-teen-red">{player.games_lost}L</span>
                          {' '}
                          <span className="text-teen-gold">{player.games_drawn}D</span>
                        </div>
                      </div>
                      
                      <Badge className={`text-lg font-bold px-3 py-1 ${
                        index === 0 ? 'bg-gradient-to-r from-teen-gold to-yellow-500 text-black' :
                        index === 1 ? 'bg-gradient-to-r from-slate-400 to-zinc-400 text-black' :
                        index === 2 ? 'bg-gradient-to-r from-orange-500 to-amber-600 text-white' :
                        'bg-primary/20 text-primary border-primary/30'
                      }`}>
                        {player.elo_rating}
                      </Badge>
                    </div>
                  </div>
                ))}
                
                {allPlayers.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    No players found. Be the first to play and claim the top spot!
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </ChessAppLayout>
  );
}
